import type { MsaasJobListingResponse, OfferingFit } from './types';

// How a Buying-tab match percentage is built (plan R20/R20b, DD8/DD13). A component the buyer gave
// no input for is left out, and its weight moves to the AI fit.
const BASE_WEIGHTS: Record<string, { ai: number; coverage: number; proximity: number }> = {
  ONSITE: { ai: 0.6, coverage: 0, proximity: 0.4 },
  PARTIAL_REMOTE: { ai: 0.4, coverage: 0.2, proximity: 0.4 },
  REMOTE: { ai: 0.8, coverage: 0.2, proximity: 0 },
};

export type FitComponentKey = 'ai' | 'coverage' | 'proximity';

export type FitComponentRow = {
  key: FitComponentKey;
  label: string;
  // 0-1, null when the component could not be scored.
  score: number | null;
  // 0-1 share of the percentage; only components that count have one.
  weight: number;
  detail?: string;
};

export const percentOf = (score: number | null | undefined): string | null =>
  score != null && Number.isFinite(score) ? `${Math.round(score * 100)}%` : null;

export const fitPercentLabel = (fit: OfferingFit | undefined): string | null =>
  fit?.percent != null && Number.isFinite(fit.percent) ? `${Math.round(fit.percent)}%` : null;

export const weightLabel = (weight: number) => `${Math.round(weight * 100)}% of match`;

export const sellerRadiusMiles = (listing: MsaasJobListingResponse): number | null => {
  const radii = (listing.geographies ?? []).map((geography) => geography.radiusMiles);
  return radii.length > 0 ? Math.max(...radii) : null;
};

export const fitComponentRows = (listing: MsaasJobListingResponse): FitComponentRow[] => {
  const fit = listing.fit;
  if (!fit) return [];
  const base = BASE_WEIGHTS[listing.workLocation] ?? BASE_WEIGHTS.REMOTE;
  const usesCoverage = base.coverage > 0 && fit.components.coverage != null;
  const usesProximity = base.proximity > 0 && fit.components.proximity != null;
  const coverageWeight = usesCoverage ? base.coverage : 0;
  const proximityWeight = usesProximity ? base.proximity : 0;
  const rows: FitComponentRow[] = [
    {
      key: 'ai',
      label: 'Provider fit',
      score: fit.components.aiFit,
      weight: 1 - coverageWeight - proximityWeight,
      detail: 'Specialty and subspecialty match',
    },
  ];
  if (usesCoverage) {
    const { coverageMatched, coverageRequired, coverageVerified } = fit.components;
    rows.push({
      key: 'coverage',
      label: 'State coverage',
      score: fit.components.coverage,
      weight: coverageWeight,
      detail:
        `${coverageMatched} of ${coverageRequired} state${coverageRequired === 1 ? '' : 's'}` +
        (coverageVerified > 0 ? ` (${coverageVerified} verified by provider license)` : ''),
    });
  }
  if (usesProximity) {
    rows.push({
      key: 'proximity',
      label: 'Proximity',
      score: fit.components.proximity,
      weight: proximityWeight,
      detail: distanceLabel(fit) ?? undefined,
    });
  }
  return rows;
};

// The distance always names where it was measured from (DD8).
export const distanceLabel = (fit: OfferingFit | undefined): string | null => {
  if (fit?.distanceMiles == null) return null;
  const source = fit.distanceSource === 'PROVIDER' ? 'Nearest provider' : 'Seller location';
  return `${source} · ${Math.round(fit.distanceMiles)} mi`;
};
